/**
 * Section 區段元件
 *
 * 提供統一的工作流程區段樣式：
 * - 標題和說明
 * - 顯示/隱藏狀態
 * - 載入狀態
 * - 摺疊/展開功能
 */

import { escapeHtml } from '@/utils/security';

// ========================================================================
// 型別定義
// ========================================================================

export interface SectionOptions {
  /** 區段標題 */
  title: string;
  /** 區段說明（可選） */
  description?: string;
  /** 步驟編號（可選） */
  stepNumber?: number;
  /** 初始是否顯示 */
  visible?: boolean;
  /** 是否可摺疊 */
  collapsible?: boolean;
  /** 初始是否摺疊 */
  collapsed?: boolean;
  /** 額外的 CSS 類別 */
  className?: string;
}

// ========================================================================
// Section 類別
// ========================================================================

export class Section {
  private element: HTMLElement;
  private header: HTMLDivElement;
  private content: HTMLDivElement;
  private loadingOverlay: HTMLDivElement | null = null;
  private options: Required<Omit<SectionOptions, 'description' | 'stepNumber'>> & Pick<SectionOptions, 'description' | 'stepNumber'>;
  private isCollapsed: boolean;
  private isVisible: boolean;

  constructor(options: SectionOptions) {
    this.options = {
      title: options.title,
      description: options.description,
      stepNumber: options.stepNumber,
      visible: options.visible ?? true,
      collapsible: options.collapsible ?? false,
      collapsed: options.collapsed ?? false,
      className: options.className ?? '',
    };

    this.isCollapsed = this.options.collapsed;
    this.isVisible = this.options.visible;

    this.element = this.createElement();
    this.header = this.createHeader();
    this.content = this.createContent();

    this.element.appendChild(this.header);
    this.element.appendChild(this.content);

    this.updateVisibility();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  /**
   * 取得整個區段元素
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * 取得內容區域（用於掛載子元件）
   */
  getContent(): HTMLDivElement {
    return this.content;
  }

  /**
   * 設定區段內容
   */
  setContent(content: string | HTMLElement): void {
    if (typeof content === 'string') {
      this.content.innerHTML = content;
    } else {
      this.content.innerHTML = '';
      this.content.appendChild(content);
    }
  }

  /**
   * 顯示區段
   */
  show(): void {
    this.isVisible = true;
    this.updateVisibility();
  }

  /**
   * 隱藏區段
   */
  hide(): void {
    this.isVisible = false;
    this.updateVisibility();
  }

  /**
   * 切換顯示/隱藏
   */
  toggle(): void {
    this.isVisible = !this.isVisible;
    this.updateVisibility();
  }

  /**
   * 展開內容
   */
  expand(): void {
    if (!this.options.collapsible) return;
    this.isCollapsed = false;
    this.updateCollapsedState();
  }

  /**
   * 摺疊內容
   */
  collapse(): void {
    if (!this.options.collapsible) return;
    this.isCollapsed = true;
    this.updateCollapsedState();
  }

  /**
   * 切換摺疊狀態
   */
  toggleCollapse(): void {
    if (!this.options.collapsible) return;
    this.isCollapsed = !this.isCollapsed;
    this.updateCollapsedState();
  }

  /**
   * 顯示載入中狀態
   * 🔒 安全性：轉義 message 防止 XSS
   */
  showLoading(message?: string): void {
    if (!this.loadingOverlay) {
      this.loadingOverlay = document.createElement('div');
      this.loadingOverlay.className = [
        'absolute',
        'inset-0',
        'bg-white',
        'bg-opacity-75',
        'flex',
        'items-center',
        'justify-center',
        'z-10',
        'rounded-lg',
      ].join(' ');
    }

    // 🔒 安全性：轉義 message
    const safeMessage = message ? escapeHtml(message) : '';

    this.loadingOverlay.innerHTML = `
      <div class="text-center">
        <div class="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
        ${safeMessage ? `<p class="mt-2 text-sm text-gray-600">${safeMessage}</p>` : ''}
      </div>
    `;

    this.element.style.position = 'relative';
    this.element.appendChild(this.loadingOverlay);
  }

  /**
   * 隱藏載入中狀態
   */
  hideLoading(): void {
    this.loadingOverlay?.remove();
  }

  /**
   * 設定標題
   */
  setTitle(title: string): void {
    const titleEl = this.header.querySelector('h2, h3');
    if (titleEl) {
      titleEl.textContent = title;
    }
  }

  /**
   * 掛載到容器
   */
  mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }

  /**
   * 銷毀區段
   */
  destroy(): void {
    this.element.remove();
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  private createElement(): HTMLElement {
    const section = document.createElement('section');
    section.className = `bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${this.options.className}`;
    return section;
  }

  private createHeader(): HTMLDivElement {
    const { title, description, stepNumber, collapsible } = this.options;

    const header = document.createElement('div');
    header.className = 'px-6 py-4 border-b border-gray-100 bg-gray-50';

    if (collapsible) {
      header.classList.add('cursor-pointer', 'hover:bg-gray-100', 'transition-colors');
      header.addEventListener('click', () => this.toggleCollapse());
    }

    header.innerHTML = `
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          ${stepNumber !== undefined ? `
            <span class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600 font-semibold text-sm">
              ${stepNumber}
            </span>
          ` : ''}
          <div>
            <h2 class="text-lg font-semibold text-gray-900">${title}</h2>
            ${description ? `<p class="text-sm text-gray-500 mt-0.5">${description}</p>` : ''}
          </div>
        </div>
        ${collapsible ? `
          <svg class="w-5 h-5 text-gray-400 transform transition-transform ${this.isCollapsed ? '' : 'rotate-180'}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        ` : ''}
      </div>
    `;

    return header;
  }

  private createContent(): HTMLDivElement {
    const content = document.createElement('div');
    content.className = 'p-6';

    if (this.isCollapsed) {
      content.classList.add('hidden');
    }

    return content;
  }

  private updateVisibility(): void {
    if (this.isVisible) {
      this.element.classList.remove('hidden');
    } else {
      this.element.classList.add('hidden');
    }
  }

  private updateCollapsedState(): void {
    const icon = this.header.querySelector('svg');

    if (this.isCollapsed) {
      this.content.classList.add('hidden');
      icon?.classList.remove('rotate-180');
    } else {
      this.content.classList.remove('hidden');
      icon?.classList.add('rotate-180');
    }
  }
}

// ========================================================================
// 工作流程區段
// ========================================================================

export interface WorkflowSectionOptions extends SectionOptions {
  /** 步驟狀態 */
  status?: 'pending' | 'active' | 'completed';
}

export class WorkflowSection extends Section {
  private status: 'pending' | 'active' | 'completed';

  constructor(options: WorkflowSectionOptions) {
    super(options);
    this.status = options.status ?? 'pending';
    this.updateStatus();
  }

  /**
   * 設定步驟狀態
   */
  setStatus(status: 'pending' | 'active' | 'completed'): void {
    this.status = status;
    this.updateStatus();
  }

  /**
   * 取得步驟狀態
   */
  getStatus(): 'pending' | 'active' | 'completed' {
    return this.status;
  }

  private updateStatus(): void {
    const element = this.getElement();

    // 移除所有狀態類別
    element.classList.remove(
      'border-gray-200',
      'border-primary-500',
      'border-green-500',
      'opacity-50'
    );

    switch (this.status) {
      case 'pending':
        element.classList.add('border-gray-200', 'opacity-50');
        break;
      case 'active':
        element.classList.add('border-primary-500');
        break;
      case 'completed':
        element.classList.add('border-green-500');
        break;
    }
  }
}
